import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGame } from "../contexts/GameContext";
import { CHEST_RARITIES } from "../data/chests";
import { TopBar } from "../components/game/TopBar";
import { STAT_NAMES_FULL } from "../data/classes";
import { Gift, Sparkles, Coins, Check } from "lucide-react";
import { SFX } from "../utils/audio";
import { ITEMS } from "../data/items";

const ChestRoom = () => {
  const { currentEncounter, takeChestRewards, character } = useGame();
  const [opened, setOpened] = useState(false);
  const navigate = useNavigate();

  if (!currentEncounter || currentEncounter.type !== "chest" || !character) {
    navigate("/map");
    return null;
  }

  const { content } = currentEncounter;
  const rarityCfg = CHEST_RARITIES[content.rarity];

  const handleOpen = () => {
    SFX.chest();
    setOpened(true);
  };

  const handleClaim = () => {
    takeChestRewards(content);
    navigate("/map");
  };

  const isPrismatic = content.rarity === "prismatique";

  return (
    <div className="h-screen w-screen bg-slate-900 bg-dots flex flex-col overflow-hidden">
      <TopBar />
      <div className="flex-1 overflow-y-auto p-6 relative flex">
        <div className="absolute top-10 left-10 w-72 h-72 bg-yellow-400/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-72 h-72 bg-fuchsia-500/20 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-xl w-full text-center m-auto">
          <h2 className="font-display text-3xl font-bold mb-2">Un coffre apparaît !</h2>
          <p className="text-slate-300 mb-6">Rareté : <span className={`font-bold uppercase ${isPrismatic ? "animate-prismatic" : ""}`} style={{ color: isPrismatic ? "#fff" : undefined }}>{rarityCfg.nom}</span></p>

          <div
            data-testid="chest-display"
            onClick={!opened ? handleOpen : undefined}
            className={`mx-auto w-56 h-56 rounded-3xl ${rarityCfg.couleur} border-4 ${rarityCfg.borderColor} cartoon-shadow-lg-static flex items-center justify-center mb-6 ${!opened ? "cursor-pointer hover:scale-105 animate-float" : ""} ${isPrismatic ? "animate-prismatic" : ""} transition-transform`}
          >
            {opened ? (
              <Sparkles className="w-32 h-32 text-white drop-shadow-[2px_2px_0_rgba(0,0,0,1)]" />
            ) : (
              <Gift className={`w-32 h-32 ${rarityCfg.textColor} drop-shadow-[2px_2px_0_rgba(0,0,0,1)]`} />
            )}
          </div>

          {!opened ? (
            <button
              data-testid="open-chest-btn"
              onClick={handleOpen}
              className="bg-yellow-400 text-slate-950 font-display font-bold uppercase tracking-wider py-3 px-8 rounded-2xl border-4 border-slate-950 cartoon-shadow"
            >
              Ouvrir le coffre
            </button>
          ) : (
            <div className="bg-slate-800 border-4 border-slate-950 rounded-3xl p-5 cartoon-shadow-lg-static animate-slide-up">
              <h3 className="font-display font-bold text-lg mb-3 text-yellow-400">🎁 Récompenses</h3>
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between bg-yellow-400/20 border-2 border-yellow-400 rounded-xl px-3 py-2">
                  <span className="flex items-center gap-2 font-bold"><Coins className="w-4 h-4 text-yellow-400" /> Or</span>
                  <span className="font-display font-bold text-yellow-400">+{content.gold}</span>
                </div>
                {Object.entries(content.stats).map(([key, val]) => (
                  <div key={key} data-testid={`chest-reward-${key}`} className="flex items-center justify-between bg-sky-400/20 border-2 border-sky-400 rounded-xl px-3 py-2">
                    <span className="font-bold">{STAT_NAMES_FULL[key]}</span>
                    <span className="font-display font-bold text-sky-400">+{val}{(key === "crit" || key === "precision") ? "%" : ""}</span>
                  </div>
                ))}
                {content.item && ITEMS[content.item] && (
                  <div data-testid={`chest-reward-item`} className="flex items-center justify-between bg-fuchsia-400/20 border-2 border-fuchsia-400 rounded-xl px-3 py-2">
                    <span className="flex items-center gap-2 font-bold"><span className="text-xl">{ITEMS[content.item].icon}</span> {ITEMS[content.item].nom}</span>
                    <span className="font-display font-bold text-fuchsia-400 text-xs">Inventaire</span>
                  </div>
                )}
              </div>
              <button
                data-testid="claim-chest-btn"
                onClick={handleClaim}
                className="w-full bg-emerald-500 text-white font-display font-bold uppercase tracking-wider py-3 px-6 rounded-2xl border-4 border-slate-950 cartoon-shadow flex items-center justify-center gap-2"
              >
                <Check className="w-5 h-5" /> Récupérer
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChestRoom;
